import React from "react";
import PropTypes from "prop-types";

/**
 * ProfilePage — the shared profile chassis: full-width header + proportional two-column grid.
 *
 * Hosts every V2 profile engine: a full-width entity/person header band across the top, then a
 * two-column grid below it — a vertical stack of section-cards in the main column and an optional
 * right rail beside (not above) them. A dumb slot host: it knows nothing about which engine or
 * which sections fill it; the engine composes those and passes them in.
 *
 * Grid philosophy (responsive-first, NOT pixel-perfect): Alyssa's reference frame is ~69/31
 * (684/309 at 1440). We capture that *proportion*, not the pixels, with Bootstrap `col-md-8` /
 * `col-md-4` (~67/33) so it reads like the design at the reference width and stays fluid/responsive
 * at every other. The chassis owns the grid columns — engine rails render plain content, no col
 * classes — so all 6–7 engines inherit one grid contract. When `rightRail` is omitted the main
 * column goes full width.
 *
 * The grid sits inside `.profile-content-band`, a maximum-width band shared with the header's
 * identity row, so the two align on one left edge on wide screens instead of stretching to fill
 * them. The band wraps the row rather than being applied to it, because a Bootstrap `.row` carries
 * negative inline margins that would push card edges past the band. The `header` slot is NOT
 * banded here: its banner is full-bleed by design, and the header bands its own inner content.
 *
 * The app-shell top bar and left sidebar nav are out-of-scope infra (already built).
 *
 * @param {Object} props
 * @param {React.ReactNode} props.header - The entity/person header (full-width band above the grid)
 * @param {React.ReactNode} props.children - The main-column content (the section-card stack)
 * @param {React.ReactNode} [props.rightRail] - Optional right-rail content (rail column owned by the chassis)
 * @param {boolean} [props.isLoading] - When true, renders `skeleton` instead of the page
 * @param {React.ReactNode} [props.skeleton] - Loading placeholder shown while `isLoading`
 */
function ProfilePage({ header, children, rightRail = null, isLoading = false, skeleton = null }) {
  if (isLoading) return skeleton;

  return (
    <>
      {header}
      <div className="profile-content-band">
        <div className="row mt-4">
          <div className={`profile-main-column ${rightRail ? "col-12 col-md-8 mb-3" : "col mb-3"}`}>{children}</div>
          {rightRail && <div className="profile-rail-column col-12 col-md-4 mb-3">{rightRail}</div>}
        </div>
      </div>
    </>
  );
}

ProfilePage.propTypes = {
  header: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
  rightRail: PropTypes.node,
  isLoading: PropTypes.bool,
  skeleton: PropTypes.node,
};

export default ProfilePage;
